import { useMemo } from "react";
import { DateTime } from "luxon";

export function useNowIndexes({
  events,
  now,
}: {
  now: DateTime;
  events: readonly { readonly startAt: string }[] | { startAt: string }[];
}): [number[], number] {
  return useMemo(() => {
    if (events.length === 0) {
      return [[], 0];
    }
    const firstIndexStartingAfterNow = events.findIndex((e) => {
      if (e.startAt) {
        return DateTime.fromISO(e.startAt) > now;
      }
      return false;
    });

    if (firstIndexStartingAfterNow === 0) {
      // we are before the first event

      // if first event is near in time mark it as now
      if (DateTime.fromISO(events[0].startAt) < now.plus({ hour: 1 })) {
        return [[0], 0];
      }

      return [[-1], 0];
    }

    if (firstIndexStartingAfterNow === -1) {
      const lastEvent = events[events.length - 1];
      if (DateTime.fromISO(lastEvent.startAt).plus({ hours: 2 }) > now) {
        return [[events.length - 1], events.length - 2];
      }
      return [[], events.length - 1];
    }

    const lastNowIndex = firstIndexStartingAfterNow - 1;
    const lastNowItem = events[lastNowIndex];
    const lastNowTime = DateTime.fromISO(lastNowItem.startAt).startOf("minute");
    // find first item with same time as last now item
    return [
      events
        .map((item, i) => [item, i] as const)
        .filter(([e]) => {
          return (
            Math.abs(
              DateTime.fromISO(e.startAt)
                .startOf("minute")
                .diff(lastNowTime)
                .as("minutes")
            ) < 5
          );
        })
        .map(([_, i]) => i),
      lastNowIndex - 1,
    ];
  }, [events, now]);
}
